import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { CalendarDays, Clock, MapPin, User, DollarSign } from 'lucide-react';

import { getClassQueryOptions, useGetClass } from '~entities/classes/api/get-class';
import { DAYS, type Days } from '~shared/constants/days';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';

const DAYS_MAP: Record<Days, string> = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
};

function formatTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

function formatDays(days: Array<Days>): string {
  return days.map((d) => DAYS_MAP[d] ?? d).join(', ');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

const PendingComponent = () => {
  return (
    <div className='flex h-screen items-center justify-center'>
      <div className='animate-pulse text-muted-foreground'>Loading class details...</div>
    </div>
  );
};

const ErrorComponent = ({ error }: { error: string }) => {
  return (
    <div className='flex h-screen flex-col items-center justify-center gap-4'>
      <h1 className='text-2xl font-semibold'>Class Not Found</h1>
      <p className='text-muted-foreground'>{error}</p>
    </div>
  );
};

export const Route = createFileRoute('/_public/classes/$classId')({
  loader: async ({ params, context }) => {
    try {
      return await context.queryClient.ensureQueryData(
        getClassQueryOptions(params.classId),
      );
    } catch (error) {
      // Public page renders its own not-found UI via useGetClass isError; avoid
      // loader CrashScreen for deleted classes.
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
  pendingComponent: PendingComponent,
  component: PublicClassDetailPage,
});

function PublicClassDetailPage() {
  const { classId } = Route.useParams();
  const { data: classDetails, isLoading, isError, error } = useGetClass({ classId });

  if (isLoading) {
    return <PendingComponent />;
  }

  if (isError || !classDetails) {
    return <ErrorComponent error={error?.message ?? 'Unable to load class details'} />;
  }

  const portalBase = import.meta.env.VITE_PARENT_PORTAL_URL;
  const checkoutUrl =
    typeof portalBase === 'string' && portalBase.trim().length > 0
      ? `${portalBase.replace(/\/$/, '')}/checkout?classId=${encodeURIComponent(classId)}`
      : '';

  const scheduleText = formatDays(classDetails.days as Array<Days>);
  const timeText =
    classDetails.startTime && classDetails.endTime
      ? `${formatTime(classDetails.startTime)} - ${formatTime(classDetails.endTime)}`
      : '';

  const instructorName =
    classDetails.mainTeacher || classDetails.instructor?.[0]?.fieldName || 'TBA';

  const locationName = classDetails.location?.fieldName || 'TBA';

  const ageRangeText = (() => {
    const tags = classDetails.tags ?? [];
    const ageTag = tags.find((t) =>
      t.fieldName.toLowerCase().match(/\d+\s*-\s*\d+/)
    );
    return ageTag?.fieldName ?? null;
  })();

  return (
    <div className='min-h-screen bg-background'>
      {/* Banner Image */}
      {classDetails.imageUrl && (
        <div className='relative h-64 w-full overflow-hidden md:h-80'>
          <img
            src={classDetails.imageUrl}
            alt={classDetails.title}
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-gradient-to-t from-black/60 to-transparent' />
        </div>
      )}

      <div className='mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8'>
        {/* Class Title and Banner Description */}
        <div className='mb-8'>
          <h1 className='mb-4 text-3xl font-bold tracking-tight md:text-4xl'>
            {classDetails.title}
          </h1>
          {classDetails.description && (
            <p className='text-lg text-muted-foreground'>{classDetails.description}</p>
          )}
        </div>

        {/* Class Details Card */}
        <Card className='mb-6'>
          <CardContent className='grid gap-6 p-6'>
            {/* Schedule */}
            {scheduleText && (
              <div className='flex items-start gap-3'>
                <CalendarDays className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
                <div>
                  <p className='font-medium'>Schedule</p>
                  <p className='text-sm text-muted-foreground'>
                    {scheduleText}
                    {timeText && ` • ${timeText}`}
                  </p>
                </div>
              </div>
            )}

            {/* Instructor */}
            <div className='flex items-start gap-3'>
              <User className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
              <div>
                <p className='font-medium'>Instructor</p>
                <p className='text-sm text-muted-foreground'>{instructorName}</p>
              </div>
            </div>

            {/* Location */}
            <div className='flex items-start gap-3'>
              <MapPin className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
              <div>
                <p className='font-medium'>Location</p>
                <p className='text-sm text-muted-foreground'>{locationName}</p>
              </div>
            </div>

            {/* Tuition */}
            <div className='flex items-start gap-3'>
              <DollarSign className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
              <div>
                <p className='font-medium'>Tuition</p>
                <p className='text-sm text-muted-foreground'>
                  {formatCurrency(classDetails.tuitionFee)}
                  {classDetails.tuitionBillingCycle &&
                    ` / ${classDetails.tuitionBillingCycle}`}
                </p>
              </div>
            </div>

            {/* Age Range (if available) */}
            {ageRangeText && (
              <div className='flex items-start gap-3'>
                <Clock className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
                <div>
                  <p className='font-medium'>Age Range</p>
                  <p className='text-sm text-muted-foreground'>{ageRangeText}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA Sign Up Button */}
        <div className='flex flex-col items-center gap-4'>
          {!checkoutUrl ? (
            <Button size='lg' className='px-8 text-base font-semibold' disabled>
              Sign up
            </Button>
          ) : (
            <Button asChild size='lg' className='px-8 text-base font-semibold'>
              <a href={checkoutUrl} target='_blank' rel='noopener noreferrer'>
                Sign up
              </a>
            </Button>
          )}
          {!checkoutUrl ? (
            <p className='text-center text-sm text-muted-foreground'>
              Checkout link is not configured.
            </p>
          ) : null}
        </div>

        {/* Remaining Seats (if available) */}
        {classDetails.remainingSeats !== undefined && classDetails.remainingSeats > 0 && (
          <p className='mt-4 text-center text-sm text-muted-foreground'>
            {classDetails.remainingSeats} spots remaining
          </p>
        )}
      </div>
    </div>
  );
}